import { createContext, ReactNode, useCallback, use, useMemo } from "react";

interface MaterialExclusionContextValue {
  materialExcludedPeriodIds: Set<number>;
  toggleMaterialExclusion: (periodIds: number[]) => void;
  areAllMaterialExcluded: (periodIds: number[]) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const NOOP_VALUE: MaterialExclusionContextValue = {
  materialExcludedPeriodIds: new Set(),
  toggleMaterialExclusion: noop,
  areAllMaterialExcluded: () => false,
};

const MaterialExclusionContext =
  createContext<MaterialExclusionContextValue | null>(null);

export const useMaterialExclusion = (): MaterialExclusionContextValue => {
  const context = use(MaterialExclusionContext);
  return context ?? NOOP_VALUE;
};

export const MaterialExclusionProvider = ({
  materialExcludedPeriodIds,
  setMaterialExcludedPeriodIds,
  children,
}: {
  materialExcludedPeriodIds: Set<number>;
  setMaterialExcludedPeriodIds: React.Dispatch<
    React.SetStateAction<Set<number>>
  >;
  children: ReactNode;
}) => {
  const toggleMaterialExclusion = useCallback(
    (periodIds: number[]) => {
      setMaterialExcludedPeriodIds((prev) => {
        const next = new Set(prev);
        const allExcluded = periodIds.every((id) => next.has(id));
        if (allExcluded) {
          periodIds.forEach((id) => next.delete(id));
        } else {
          periodIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [setMaterialExcludedPeriodIds]
  );

  const areAllMaterialExcluded = useCallback(
    (periodIds: number[]) =>
      periodIds.length > 0 &&
      periodIds.every((id) => materialExcludedPeriodIds.has(id)),
    [materialExcludedPeriodIds]
  );

  const value = useMemo(
    () => ({
      materialExcludedPeriodIds,
      toggleMaterialExclusion,
      areAllMaterialExcluded,
    }),
    [materialExcludedPeriodIds, toggleMaterialExclusion, areAllMaterialExcluded]
  );

  return (
    <MaterialExclusionContext value={value}>
      {children}
    </MaterialExclusionContext>
  );
};
